import React, { useState, useEffect, useRef } from 'react';
import * as S from './StickyTabNavigation.style';

export interface Tab {
    navigatorIndex: string;
    label: string;
}

interface StickyTabNavigationProps {
    tabs: Tab[];
    stickyOffset?: number;
}

const StickyTabNavigation: React.FC<StickyTabNavigationProps> = ({ tabs, stickyOffset = 0 }) => {
    const [activeTab, setActiveTab] = useState<string>(tabs.length > 0 ? tabs[0].navigatorIndex : '');
    const [isFixed, setIsFixed] = useState(false);
    const [navHeight, setNavHeight] = useState(0);
    const navRef = useRef<HTMLElement>(null);
    const placeholderRef = useRef<HTMLDivElement>(null);
    const initialNavTopRef = useRef<number | null>(null);

    useEffect(() => {
        // console.log('vip-nav-tabs', tabs)
        if (navRef.current) {
            setNavHeight(navRef.current.offsetHeight);
            setTimeout(() => {
                if (navRef.current) {
                    initialNavTopRef.current = navRef.current.getBoundingClientRect().top + window.pageYOffset;
                }
            }, 100);
        }
    }, []);

    const handleTabClick = (tabId: string) => {
        setActiveTab(tabId);
        const element = document.getElementById(tabId);
        if (element) {
            const currentNavHeight = navRef.current?.offsetHeight ?? 0;
            const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
            const offsetPosition = elementPosition - currentNavHeight - stickyOffset + 5; // 5 extra space

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth',
            });
        }
    };

    useEffect(() => {
        const handleScroll = () => {
            if (initialNavTopRef.current === null) return;

            const scrollTop = window.pageYOffset;
            const navTopWhenFixed = initialNavTopRef.current - stickyOffset;

            // console.log('scrollTop--', scrollTop, initialNavTopRef.current)
            if (scrollTop >= navTopWhenFixed) {
                !isFixed && setIsFixed(true);
            } else {
                isFixed && setIsFixed(false);
            }

            // update active Tab
            let currentActiveTabId = '';
            const currentNavRenderHeight = navRef.current?.offsetHeight ?? 0;

            for (const tab of tabs) {
                const element = document.getElementById(tab.navigatorIndex);
                if (element) {
                    const rect = element.getBoundingClientRect();
                    const effectiveNavHeight = isFixed ? currentNavRenderHeight : 0; // 如果固定，则tab内容从导航栏下方开始算
                    if (rect.top <= effectiveNavHeight + stickyOffset + 20 && rect.bottom >= effectiveNavHeight + stickyOffset) {
                        currentActiveTabId = tab.navigatorIndex;
                        break;
                    }
                }
            }
            if (!currentActiveTabId) {
                for (const tab of tabs) {
                    const element = document.getElementById(tab.navigatorIndex);
                    if (element) {
                        const rect = element.getBoundingClientRect();
                        if (rect.top >= 0 && rect.top <= window.innerHeight) {
                            currentActiveTabId = tab.navigatorIndex;
                            break;
                        }
                    }
                }
            }
            if ((window.innerHeight + window.pageYOffset) >= document.body.offsetHeight - 2) {
                const lastTab = tabs[tabs.length - 1];
                if (lastTab) {
                    const lastElement = document.getElementById(lastTab.navigatorIndex);
                    if (lastElement && lastElement.getBoundingClientRect().top < window.innerHeight) {
                        currentActiveTabId = lastTab.navigatorIndex;
                    }
                }
            }
            if (currentActiveTabId && currentActiveTabId !== activeTab) {
                setActiveTab(currentActiveTabId);
            }
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll();
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [tabs, activeTab, stickyOffset, isFixed]);

    useEffect(() => {
        if (placeholderRef.current) {
            placeholderRef.current.style.display = isFixed ? 'block' : 'none';
        }
    }, [isFixed]);

    if (!tabs || tabs.length === 0) {
        return null;
    }

    return (
        <>
            <S.NavPlaceholder ref={placeholderRef} height={navHeight} />
            <S.NavWrapper ref={navRef} isFixed={isFixed} fixedTop={stickyOffset}>
                <S.TabList>
                    {tabs.map((tab) => (
                        <S.TabItem
                            key={tab.navigatorIndex}
                            isActive={activeTab === tab.navigatorIndex}
                            onClick={() => handleTabClick(tab.navigatorIndex)}
                        >
                            {tab.label}
                        </S.TabItem>
                    ))}
                </S.TabList>
            </S.NavWrapper>
        </>
    );
};

export default StickyTabNavigation;
